import React from 'react';
import { Send, Paperclip, X, FileText, Link } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { config } from '../../config';

interface MessageComposerProps {
  onSendMessage: (content: string, attachments?: File[]) => void;
  onUploadUrl?: (url: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
  onSendMessage,
  onUploadUrl,
  disabled = false,
  placeholder = 'Type your message...',
}) => {
  const [message, setMessage] = React.useState('');
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const [isDragging, setIsDragging] = React.useState(false);
  const [urlModalOpen, setUrlModalOpen] = React.useState(false);
  const [urlInput, setUrlInput] = React.useState('');
  const [urlError, setUrlError] = React.useState('');
  const [urlLoading, setUrlLoading] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  };

  const openUrlModal = () => {
    if (!onUploadUrl) return;
    setUrlInput('');
    setUrlError('');
    setUrlModalOpen(true);
  };

  const closeUrlModal = () => {
    if (!urlLoading) {
      setUrlModalOpen(false);
      setUrlInput('');
      setUrlError('');
    }
  };

  const handleSubmitUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onUploadUrl) return;
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setUrlError('Please enter a URL.');
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      setUrlError('URL must start with http:// or https://');
      return;
    }
    setUrlError('');
    setUrlLoading(true);
    try {
      await onUploadUrl(trimmed);
      closeUrlModal();
      toast.success('URL added. Processing may take a moment.');
    } catch (error) {
      const errMessage = error instanceof Error ? error.message : 'Failed to ingest URL';
      toast.error(errMessage);
    } finally {
      setUrlLoading(false);
    }
  };

  React.useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() && attachments.length === 0) return;
    
    // Prepend file names to the message in the format "File Uploaded: filename.xyz - User Text"
    let messageToSend = message.trim();
    if (attachments.length > 0) {
      const fileNames = attachments.map(file => file.name).join(', ');
      messageToSend = messageToSend 
        ? `File Uploaded: ${fileNames} - ${messageToSend}`
        : `File Uploaded: ${fileNames}`;
    }
    
    onSendMessage(messageToSend, attachments);
    setMessage('');
    setAttachments([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
  };

  const addFiles = (files: File[]) => {
    const validFiles = files.filter((file) => {
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'text/plain',
        'text/markdown',
        'application/json',
        'text/csv',
        'text/tab-separated-values',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/html',
        'application/x-yaml',
        'text/yaml',
        'text/xml',
        'application/xml',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/rtf',
        'text/rtf',
        'application/vnd.oasis.opendocument.text',
        'application/vnd.oasis.opendocument.spreadsheet',
        'application/vnd.oasis.opendocument.presentation',
      ];
      
      const maxSize = config.maxFileSizeBytes;
      
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Unsupported file type`);
        return false;
      }
      
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large (max ${config.maxFileSizeMB}MB)`);
        return false;
      }
      
      return true;
    });

    setAttachments((prev) => [...prev, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const getFileIcon = () => <FileText className="h-4 w-4" />;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="p-3 sm:p-4">
      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="mb-3 space-y-2">
          {attachments.map((file, index) => (
            <div
              key={index}
              className="flex items-center space-x-2 p-2 bg-gray-50 border border-gray-200 rounded-lg"
            >
              {getFileIcon()}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon={X}
                onClick={() => removeAttachment(index)}
                className="text-gray-400 hover:text-red-600"
              />
            </div>
          ))}
        </div>
      )}

      {/* Composer */}
      <form onSubmit={handleSubmit} className="relative">
        <div
          className={`relative border rounded-lg transition-colors ${
            isDragging
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 focus-within:border-primary-500'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDragging && (
            <div className="absolute inset-0 bg-primary-50 border-2 border-dashed border-primary-500 rounded-lg flex items-center justify-center z-10">
              <div className="text-center">
                <Paperclip className="h-8 w-8 text-primary-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-primary-900">
                  Drop files here to attach
                </p>
                <p className="text-xs text-primary-700">
                  PDF, DOCX, TXT, MD, JSON, CSV, XLSX, HTML, YAML, XML, PPTX, RTF, ODT/ODS/ODP (max {config.maxFileSizeMB}MB)
                </p>
              </div>
            </div>
          )}

          <div className="flex items-end space-x-2 p-2 sm:p-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={Paperclip}
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="flex-shrink-0"
              title="Attach files (PDF, DOCX, TXT, etc.)"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={Link}
              onClick={openUrlModal}
              disabled={disabled || !onUploadUrl}
              className="flex-shrink-0 hidden sm:inline-flex"
              title="Add URL (paste a link)"
            />

            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className="flex-1 resize-none border-none focus:ring-0 focus:outline-none bg-transparent text-sm sm:text-base max-h-48 min-h-[20px]"
              rows={1}
            />

            <Button
              type="submit"
              size="sm"
              icon={Send}
              disabled={disabled || (!message.trim() && attachments.length === 0)}
              className="flex-shrink-0"
            />
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.txt,.md,.markdown,.json,.csv,.tsv,.xlsx,.xlsm,.html,.htm,.yaml,.yml,.xml,.xsd,.pptx,.pptm,.rtf,.odt,.ods,.odp,.ott,.ots,.otp"
          onChange={handleFileSelect}
          className="hidden"
        />
      </form>

      {/* Add URL modal */}
      <Modal
        isOpen={urlModalOpen}
        onClose={closeUrlModal}
        title="Add URL"
        maxWidth="md"
      >
        <form onSubmit={handleSubmitUrl} className="space-y-4">
          <Input
            type="url"
            label="URL"
            placeholder="https://example.com/page"
            value={urlInput}
            onChange={(e) => {
              setUrlInput(e.target.value);
              if (urlError) setUrlError('');
            }}
            error={urlError}
            disabled={urlLoading}
            autoFocus
            autoComplete="url"
          />
          <p className="text-sm text-gray-500">
            Paste a URL. Only http:// and https:// are supported.
          </p>
          <p className="text-sm text-gray-500 italic">
            Note: Not all URLs are fully crawlable. The agent will try its best to extract the available content.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeUrlModal}
              disabled={urlLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              icon={Link}
              loading={urlLoading}
              disabled={urlLoading}
            >
              Add URL
            </Button>
          </div>
        </form>
      </Modal>

      {/* Tips */}
      <div className="mt-2 text-xs text-gray-500 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-0">
        <span className="hidden sm:inline">Press Enter to send, Shift + Enter for new line</span>
        <span className="text-xs">Supports PDF, DOCX, TXT, MD, JSON, CSV, XLSX, HTML, YAML, XML, PPTX, RTF, ODT/ODS/ODP, URLs</span>
      </div>
    </div>
  );
};

export default MessageComposer;
